import { BridgeCreateAppointmentModal, MODAL_BOOKING } from '@/scheduling/components/appointments/create-appointment-modal';
import { useMemo, useState, useCallback, useEffect, useRef, type ComponentProps } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { addDays, startOfWeek } from 'date-fns';
import { toast } from 'sonner';
import { FrameScheduling } from '@/components/frames';
import {
  ViewAppointmentPopup,
  type ViewAppointmentTarget,
} from '@/scheduling/components/appointments/view-appointment-popup';
import { useSchedulingStore } from '@/scheduling/store/scheduling-store';
import { useStatusList } from '@/services/scheduling/use-status-list';
import { useAvailableProviders } from '@/services/scheduling/use-available-providers';
import { useCalendarSlots } from '@/scheduling/services/use-calendar-slots';
import { isPracticeSwitchOn } from '@/shared/lib/practice-switches';
import { useExamRooms } from '@/scheduling/services/use-exam-rooms';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { useAppointments } from '@/scheduling/services/use-appointments';
import { useDeviceAppointments } from '@/scheduling/services/use-device-appointments';
import {
  appointmentsQueryKey,
  deviceAppointmentsQueryKey,
} from '@/scheduling/services/appointment-list-keys';
import { useRescheduleAppointment } from '@/scheduling/services/use-reschedule-appointment';
import { useDeviceReschedule } from '@/scheduling/services/use-device-reschedule';
import { useCalendarDevices } from '@/scheduling/services/use-calendar-devices';
import { useDeviceAvailability } from '@/scheduling/services/use-device-availability';
import { useDeviceCalendarSlots } from '@/scheduling/services/use-device-calendar-slots';
import {
  CALENDAR_ACTOR_PARAM,
  clampDeviceView,
  parseCalendarActor,
  resolveCalendarActor,
  writeCalendarActor,
  type CalendarActor,
} from '@/scheduling/lib/calendar-actor';
import {
  DEVICE_SUBTYPE_FILTER_ENABLED,
  deviceIdFromParticipants,
  deviceSubtypes,
  deviceTabBadgeCount,
  filterBlockableDevices,
  filterBookableDevices,
  filterDevicesBySubtype,
  groupRowsByDevice,
  isBookableDevice,
} from '@/scheduling/lib/device-columns';
import {
  hoursCoveringWindows,
  parseAvailability,
  windowsForDay,
} from '@/scheduling/lib/device-availability';
import { EMPTY_GUID } from '@/shared/lib/empty-guid';
import { getTimeFormat } from '@/shared/lib/time-format';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useOrgLocations } from '@/scheduling/services/use-org-locations';
import { useOrganisationFeatures } from '@/shared/services/use-organisation-features';
import { usePracticeWorkingHours } from '@/scheduling/services/use-practice-working-hours';
import { useSchedulingRealtime } from '@/scheduling/realtime/use-scheduling-realtime';
import {
  appointmentProviderIds,
  devicesFromDeviceCalendarSlots,
  enrichProviders,
  filterAppointmentsByInvoiceStatus,
  filterAppointmentsByLocation,
  filterAppointmentsByStatus,
  hoursForDayList,
  mapAppointmentsToDayList,
  mapAppointmentsToDayTiles,
  mapAppointmentsToWeekTiles,
  providerMatchesSpecialties,
  providersFromCalendarSlots,
  practiceSlotsOptions,
  calendarSlotsState,
  specialtiesFromCalendarSlots,
  specialtiesFromProviders,
  type DayListRow,
} from '@/scheduling/lib/calendar-mapping';
import { routeAppointmentDrop } from '@/scheduling/lib/drop-routing';
import {
  buildMonthGrid,
  toLocalISO,
  type MonthCalendarDay,
  type MonthEvent,
} from '@/components/blocks/month-calendar';
import { useAuthStore } from '@/auth/auth-store';
import { useDevicesEnabled } from '@/settings/services/use-clinic-services';
import { usePermission } from '@/auth/permission-gate';
import { DOCTOR_ROLE } from '@/auth/roles';
import { useCreateAppointmentHandoff } from '@/shared/store/create-appointment-handoff';
import {
  buildFollowUpInitialFromAppointment,
  followUpSeedDateISO,
} from '@/scheduling/lib/build-follow-up-from-appointment';
import { dateFilterToCalendarView } from '@/scheduling/lib/date-filter-mapping';
import type { ProviderSummary } from '@/components/blocks/provider-strip';
import type { CalendarAppointment } from '@/components/blocks/week-calendar';
import type {
  AppointmentResponse,
  AppointmentsDetailsResponse,
  Coding,
  DateFilter,
  ListAppointmentResponse,
  MonthBlockAppointment,
} from '@/types/scheduling-api';
// (ListAppointment / AppointmentStatusType imports removed —
//  the page no longer ships any list-view fixtures.)

/**
 * SchedulingPage — Page (data layer)
 * Thin wrapper that connects FrameScheduling to real API hooks.
 * All UI composition lives in FrameScheduling and its blocks.
 *
 * Data flow:
 *   useStatusList         → statusLegend
 *   useAvailableProviders → provider strip
 *   useCalendarSlots      → calendar grid tiles + strip enrichment
 *   useAppointments       → calendar grid tiles (with patient names)
 *   useDeviceAppointments → the SAME tiles on the Devices tab, off the device
 *                           route (PROD-2382)
 */

/**
 * Patient search returns nothing until the real /api/Patient/search endpoint
 * is wired (separate ticket). Keeps the typeahead in CreateAppointmentPanel
 * functional with no client-side fixtures.
 */
const noopSearchPatients = async () => [];

/**
 * The "All" selection for the device subtype filter — a module constant so the
 * hidden-filter branch hands the same array identity every render and the
 * column memo below it does not recompute on every keystroke elsewhere.
 */
const NO_SUBTYPES: string[] = [];

/**
 * The same thing for the practitioner specialty chips, and for the same reason:
 * one stable array identity, so the Devices branch below does not invalidate
 * the specialty memos on every render.
 */
const NO_SPECIALTIES: string[] = [];

/**
 * Local YYYY-MM-DD — avoids the toISOString() UTC shift that shifts the
 * date by a day for users east of UTC near midnight.
 */
function toLocalISODate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function SchedulingPage() {
  const navigate = useNavigate();
  // Tenant context — sourced strictly from the authenticated user (PROD-773).
  // Empty string flows safely through downstream hooks (all gate on
  // `enabled: !!practiceId`), so the page renders the shell while the
  // dev-bootstrap auto-login completes, then auto-refetches.
  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';
  const [practiceSlotsOn] = useState(() => isPracticeSwitchOn('calendar-practice-slots'));

  // Org feature flags — Flutter `calendar_tab.dart` fires
  // GetOrganisationFeatureEvent on calendar-tab init. Replicated verbatim:
  // prime the cache on calendar mount. Fetch-but-don't-gate (Flutter stores
  // the result but no widget branches on it yet); see the hook header.
  useOrganisationFeatures(orgId);

  // PROD-1030 (F-SCH-132 sibling, doctor case): a doctor's calendar defaults
  // to their own column — Flutter `calendar_screen.dart:2048-2050` fetches
  // `providerId = [userData.id]` for doctorRole. org-admin/staff fetch all.
  // NOTE: the nurse case (`:2052-2056` uses `nurseAssignedProviders`) is blocked
  // on the AssignedProviders endpoint, currently unwired in React (F-SET-044) —
  // left as the full roster until that lands. String() coerces a numeric role.
  const userRole = useAuthStore((s) => s.user?.role);
  const userSetupId = useAuthStore((s) => s.user?.id);

  // Provider-name → provider settings (Flutter `calendar_tab.dart:4215-4238`:
  // tapping a provider's name in the strip pushes `DoctorBaseWidget`, gated on
  // `validatePermission([Manage_Doctors], view: true)`). React target is the
  // practitioner edit route `/settings/practitioner/:doctorSetupId`; the
  // calendar provider's `id` IS the doctorSetupId (Flutter passes `doc.id`
  // straight into `doctorSetupId`). Handler is only wired when permitted, so
  // without the permission the name renders as plain, non-clickable text.
  const canManageDoctors = usePermission(['Manage_Doctors'], { view: true });
  const handleProviderNameClick = useCallback(
    (providerId: string) => {
      if (!providerId) return;
      // `from: '/'` so the edit page's Back returns to the calendar (Flutter
      // pop parity), not the Settings practitioners list.
      navigate(`/settings/practitioner/${providerId}`, { state: { from: '/' } });
    },
    [navigate],
  );
  const selfProviderIds =
    String(userRole) === DOCTOR_ROLE && userSetupId ? [userSetupId] : undefined;

  // Calendar date + view are URL-synced (`?date=YYYY-MM-DD&view=Day`) so the
  // appointment panel's in-app Back button can return to the exact day/view
  // the user was on (see view-appointment-panel-page.tsx `goBack`), and
  // calendar links are shareable. Lazy-init from the URL, fall back to today.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const raw = searchParams.get('date');
    if (raw) {
      const [y, m, d] = raw.split('-').map(Number);
      const parsed = new Date(y, (m ?? 1) - 1, d ?? 1);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  const todayStr = toLocalISODate(selectedDate);
  // Calendar-view state must be declared before `appointmentsParams` /
  // realtime / day-vs-week derivations below all read it. (Moved up from
  // its prior position near the month-fetch block — was line ~352.)
  const [calendarView, setCalendarView] = useState<DateFilter>(() => {
    const v = searchParams.get('view');
    return v === 'Day' || v === 'Week' || v === 'Month' || v === 'Room' || v === 'Doctor'
      ? v
      : 'Day';
  });
  // Practitioners | Devices (PROD-2007). URL-synced as `?actor=devices`;
  // Practitioners is the absence of the param, so every pre-existing calendar
  // URL keeps its exact shape. The two tabs never share a grid.
  const [requestedActor, setCalendarActor] = useState<CalendarActor>(() =>
    parseCalendarActor(searchParams.get(CALENDAR_ACTOR_PARAM)),
  );
  // PROD-2845: the Devices view exists only while devices are enabled — the
  // `VITE_RESOURCES_TAB` release switch AND the organization's "books
  // devices" switch. Off, the calendar is practitioners-only: no actor tabs,
  // no Book a device, and a `?actor=devices` link reads as Practitioners.
  // The requested actor is kept, so the tab comes back once the switch read
  // lands (it reads as off while in flight).
  const { enabled: devicesEnabled, isLoading: devicesSettingLoading } = useDevicesEnabled();
  const calendarActor = resolveCalendarActor(requestedActor, devicesEnabled);
  const isDevicesTab = calendarActor === 'devices';
  // The Devices tab is "the same day/week grid": a Month / Room / Doctor view
  // left over from the Practitioners tab reads as Day here, while `calendarView`
  // itself is untouched so switching back restores it.
  const effectiveView: DateFilter = isDevicesTab ? clampDeviceView(calendarView) : calendarView;
  // Mirror the calendar's date/view into the URL (replace, so paging days
  // doesn't spam browser history). This is what makes returning to
  // `/?date=…&view=…` restore the exact day the user was on.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('date', toLocalISODate(selectedDate));
        next.set('view', calendarView);
        writeCalendarActor(next, calendarActor);
        return next;
      },
      { replace: true },
    );
  }, [selectedDate, calendarView, calendarActor, setSearchParams]);
  // Week-view bounds (Flutter parity — `calendar_tab.dart:2536-2538`):
  // startDate = Monday of the week containing `selectedDate`, endDate =
  // Sunday (start + 6). date-fns `startOfWeek` with `weekStartsOn: 1`
  // gives the Monday anchor; addDays(6) lands on Sunday. Always
  // computed (cheap) so memoised consumers below have stable refs even
  // outside Week view.
  const weekStartDate = useMemo(
    () => startOfWeek(selectedDate, { weekStartsOn: 1 }),
    [selectedDate],
  );
  const weekEndDate = useMemo(
    () => addDays(weekStartDate, 6),
    [weekStartDate],
  );
  const weekStartISO = toLocalISODate(weekStartDate);
  const weekEndISO = toLocalISODate(weekEndDate);
  const storedSpecialties = useSchedulingStore((s) => s.selectedSpecialties);
  /**
   * The specialty chip row does not render on the Devices tab (Andrew,
   * 2026-09-14 — a device has no specialty), so the persisted selection has to
   * read as "All" there, exactly as `selectedDeviceSubtypes` does below.
   *
   * The leak this closes is invisible rather than cosmetic: `providers` stays
   * the specialty-narrowed PRACTITIONER list, and the scheduling-AI bar reads
   * it as `intentCandidates` / `resolvePractitionerId` on BOTH tabs — so a chip
   * left in localStorage would quietly shrink the roster the bar can resolve,
   * with no control on screen to clear it.
   *
   * The STORE is untouched: this masks the READ, so switching back to
   * Practitioners restores the user's chips exactly as they left them.
   */
  const selectedSpecialties = isDevicesTab ? NO_SPECIALTIES : storedSpecialties;
  const selectedProviderIds = useSchedulingStore((s) => s.selectedProviderIds);
  const selectedStatusCodes = useSchedulingStore((s) => s.selectedStatusCodes);
  const selectedInvoiceStatuses = useSchedulingStore(
    (s) => s.selectedInvoiceStatuses,
  );
  const selectedLocationIds = useSchedulingStore((s) => s.selectedLocationIds);
  const openEditPanel = useSchedulingStore((s) => s.openEditPanel);

  // Practice locations for the location-filter buttons. Flutter calls
  // /api/Organization/{orgId}/Locations after login and stitches the
  // result onto the user object; we keep it as its own React Query.
  const { data: organizationLocations = [] } = useOrgLocations(
    orgId,
  );

  const { data: statuses } = useStatusList();

  // Day-scope all three calls.
  //   AvailableProviders → authoritative provider list for the day; drives
  //                        /api/Appointment's practitioners[] body.
  //   calendarSlots      → strip enrichment (workingHours / nextSlots /
  //                        appointmentProfile chips). Specialty + checkbox
  //                        filtering both run client-side off this data.
  const {
    data: apiProviders,
    isLoading: isProvidersLoading,
    isError: isProvidersError,
    isSuccess: isProvidersSuccess,
  } = useAvailableProviders({
    organizationId: orgId,
    practiceId: practiceId,
    date: todayStr,
    // Doctor → own column only (PROD-1030). undefined → full roster (all
    // other roles + the nurse case pending F-SET-044). Cascades to
    // allPractitionerIds → calendarSlots + appointments + provider strip.
    practitioners: selfProviderIds,
  });

  // Flutter parity (`scheduling_bloc.dart:703-709`): a failed OR empty
  // AvailableProviders fetch both fold to an empty list and fire
  // ShowErrorEvent("No providers available") — rendered as an error toast
  // (`calendar_screen.dart:2242-2243`). The hook opts out of the generic
  // global error toast (`meta.skipGlobalError`); we surface the verbatim
  // message here on either outcome. Keyed on practiceId+date so it fires
  // once per settled fetch (e.g. switching to a practice with no/erroring
  // providers), matching Flutter's one-event-per-fetch behaviour.
  const noProvidersAvailable =
    isProvidersError ||
    (isProvidersSuccess && (apiProviders?.length ?? 0) === 0);
  useEffect(() => {
    if (noProvidersAvailable) toast.error('No providers available');
  }, [noProvidersAvailable, practiceId, todayStr]);
  // By-Room full room list (PROD-985) — the practice's configured exam
  // rooms, so empty rooms still get a column. Lazy: only fetched when the
  // By-Room view is active (passing null disables the query otherwise).
  const { data: roomColumns = [] } = useExamRooms(
    calendarView === 'Room' ? practiceId : null,
  );
  const { data: slotsResponse, isError: isSlotsError, isFetching: isSlotsFetching, refetch: refetchSlots } = useCalendarSlots({
    organizationId: orgId,
    practiceId: practiceId,
    startDate: todayStr,
    endDate: todayStr,
  });
  const { data: practiceWorkingHours } = usePracticeWorkingHours(
    practiceId,
  );

  // Calendar grid: appointment list for today, narrowed to the providers
  // we know about (preferring calendarSlots' list over availableProviders').
  // When the user picks specialties, narrow further client-side using EVERY
  // specialty a provider carries (primary `speciality` + `doctorSpecialities`).
  // ALL practitioner ids — passed to /api/Appointment unconditionally.
  // Sourced ONLY from AvailableProviders so a slow calendarSlots response
  // never delays the grid, and so toggling specialty/provider filters
  // never refires the query (specialty + checkbox both filter client-side).
  // Sorted + memoized so the queryKey is bit-identical across re-renders.
  const allPractitionerIds = useMemo(() => {
    if (isProvidersLoading) return [];
    const ids = (apiProviders ?? []).map((p) => p.id);
    return [...ids].sort();
  }, [apiProviders, isProvidersLoading]);

  // Specialty-filtered subset — drives the provider strip and the
  // client-side filter on the cached appointments response. NOT in the
  // request body, so changing the specialty chips never refetches.
  // Prefers calendarSlots' specialty data (richer doctorSpecialities)
  // when available; otherwise reads from AvailableProviders.
  const visiblePractitionerIds = useMemo(() => {
    const useSlots =
      !isSlotsError && slotsResponse && slotsResponse.length > 0;
    const fromSource = useSlots
      ? slotsResponse
          .filter((p) => providerMatchesSpecialties(p, selectedSpecialties))
          .map((p) => p.id)
          .filter((id): id is string => Boolean(id))
      : (apiProviders ?? [])
          .filter((p) => providerMatchesSpecialties(p, selectedSpecialties))
          .map((p) => p.id);
    return [...fromSource].sort();
  }, [slotsResponse, apiProviders, selectedSpecialties, isSlotsError]);

  // After specialty + checkbox filters, the practitioner ids that should
  // actually appear in the grid. Authoritative semantic (Andrew, 2026-05-25):
  // empty `selectedProviderIds` = Unselect all = hide every appointment.
  // First-load auto-fill (above) means the empty state only happens when the
  // user explicitly clicked Unselect all. Specialty filter narrows the
  // visible-set as before; intersection with checkbox set is the final list.
  const effectivePractitionerIds = useMemo(() => {
    if (selectedProviderIds.length === 0) return [];
    const set = new Set(selectedProviderIds);
    return visiblePractitionerIds.filter((id) => set.has(id));
  }, [visiblePractitionerIds, selectedProviderIds]);

  // The API request fires once per date (+ practitioner set) and never
  // narrows for filters — every filter is applied client-side below so
  // toggling a pill / dropdown / location button gives instant UI response
  // without a refetch. Summary counts (waitingRoom/noShow/cancelled) stay
  // accurate to the full day rather than reflecting the current narrow.
  const appointmentsParams = useMemo(() => {
    if (allPractitionerIds.length === 0) return null;
    // Week view → fetch Mon–Sun. Mirrors Flutter's
    // `calendar_tab.dart:2531-2539` (filter='Week', startDate=Monday,
    // endDate=Sunday). Day view stays single-day. ByDoctor / ByRoom /
    // Month are handled by their own queries below; this hook only
    // serves Day + Week.
    if (calendarView === 'Week') {
      return {
        organizationId: orgId,
        practiceId: practiceId,
        filter: 'Week' as const,
        startDate: `${weekStartISO}T00:00:00.000`,
        endDate: `${weekEndISO}T23:59:59.000`,
        practitioners: allPractitionerIds,
      };
    }
    return {
      organizationId: orgId,
      practiceId: practiceId,
      filter: 'Day' as const,
      startDate: `${todayStr}T00:00:00.000`,
      endDate: `${todayStr}T23:59:59.000`,
      practitioners: allPractitionerIds,
    };
  }, [
    todayStr,
    weekStartISO,
    weekEndISO,
    allPractitionerIds,
    calendarView,
    orgId,
    practiceId,
  ]);
  const { data: dayAppointments, isLoading: dayAppointmentsLoading } =
    useAppointments(appointmentsParams);

  // Realtime — surgical merge of UpdateAppointments into the React Query
  // cache, plus calendar-slots invalidation on RefreshSlots. Both hubs are
  // opened lazily once the practice id is known.
  // ── Devices tab data (PROD-2007) ─────────────────────────────────────
  // Columns from the device registry; bookings from the device list route
  // (`POST /api/Appointment/ResourceList`, PROD-2382) with the device ids in
  // `devices`. They used to ride in the PRACTITIONER route's `practitioners`
  // array, which worked only because that filter matches on reference with no
  // type check — an accident, not a contract. Presence per column from
  // ResourceAvailability. Everything is gated on the tab so the Practitioners
  // tab fires nothing new.
  const {
    data: allDevices = [],
    isLoading: registryLoading,
    isSuccess: registryLoaded,
  } = useCalendarDevices(practiceId, isDevicesTab);
  // Devices tab TOP CARDS (PROD-2184) — the device-side twin of the
  // `useCalendarSlots` feed above, on the same selected-day range so
  // "N slots today" means the same thing on both tabs. The Practitioners tab
  // still fires nothing new: the gate is the hook's `enabled` argument.
  //
  // The gate USED to be `isDevicesTab` folded into the params (null params =
  // disabled). That also changed the query key, so leaving the Devices tab
  // threw away an answer the feed had already given and the tab badge lost the
  // device count it had just learned (PROD-2364). The params are now keyed on
  // the practice/day alone and `enabled` does the gating, exactly as
  // `useCalendarDevices` above already does.
  //
  // It is also the BOOKABILITY oracle for the whole tab (see below), which is
  // why it is read here, above the columns, rather than next to the strip.
  const deviceSlotsParams = useMemo(
    () =>
      practiceId
        ? {
            organizationId: orgId,
            practiceId,
            startDate: todayStr,
            endDate: todayStr,
          }
        : null,
    [orgId, practiceId, todayStr],
  );
  const {
    data: deviceSlots,
    isLoading: deviceSlotsLoading,
    isSuccess: deviceSlotsLoaded,
  } = useDeviceCalendarSlots(deviceSlotsParams, isDevicesTab);
  /**
   * The devices the tab actually shows (Andrew, 2026-09-13: "show only the
   * devices with working hours and appointment profiles"). ONE narrowing, fed
   * to the cards, the columns, the subtype options, the bookings query and the
   * Book-a-device picker alike — so no surface can offer a device another
   * surface knows is unbookable. The rule itself lives in `isBookableDevice`.
   */
  const bookableDevices = useMemo(
    () => filterBookableDevices(allDevices, deviceSlots),
    [allDevices, deviceSlots],
  );
  /**
   * The devices the BLOCK panel may offer (PROD-2454) — the same join over
   * the same two feeds, under the weaker predicate `isBlockableDevice`.
   *
   * A superset of `bookableDevices`, and deliberately so: a block asks a
   * machine for a window to mark busy and never asks it for a product, so a
   * device with working hours and no appointment profile belongs here and
   * not above. Narrowed HERE, beside its sibling, because only this page
   * holds both the registry and the slots feed — the same reason the
   * bookable narrowing lives here rather than in the picker.
   */
  const blockableDevices = useMemo(
    () => filterBlockableDevices(allDevices, deviceSlots),
    [allDevices, deviceSlots],
  );
  // The narrowing needs BOTH queries, so the grid keeps its loading state
  // until the feed lands rather than flashing 57 devices and dropping 52.
  const devicesLoading = registryLoading || deviceSlotsLoading;
  /**
   * The Devices tab badge (PROD-2364). `bookableDevices` is empty both when
   * the practice has no bookable devices AND when neither feed has answered
   * yet — the narrowing cannot tell those apart, so the page has to, from the
   * query status. Unknown renders no badge at all; see `deviceTabBadgeCount`.
   */
  const deviceCount = deviceTabBadgeCount(
    registryLoaded && deviceSlotsLoaded,
    bookableDevices,
  );
  const deviceCards = useMemo(
    () => devicesFromDeviceCalendarSlots((deviceSlots ?? []).filter(isBookableDevice), selectedDate),
    [deviceSlots, selectedDate],
  );
  const storedDeviceSubtypes = useSchedulingStore((s) => s.selectedDeviceSubtypes);
  const toggleDeviceSubtype = useSchedulingStore((s) => s.toggleDeviceSubtype);
  const clearDeviceSubtypes = useSchedulingStore((s) => s.clearDeviceSubtypes);
  // With the chip row hidden the selection has to read as "All", or a subtype
  // persisted in localStorage from before the hide would keep filtering
  // columns with no visible control to switch it off.
  const selectedDeviceSubtypes = DEVICE_SUBTYPE_FILTER_ENABLED ? storedDeviceSubtypes : NO_SUBTYPES;
  const deviceSubtypeOptions = useMemo(
    () => deviceSubtypes(bookableDevices),
    [bookableDevices],
  );
  const deviceColumns = useMemo(
    () => filterDevicesBySubtype(bookableDevices, selectedDeviceSubtypes),
    [bookableDevices, selectedDeviceSubtypes],
  );
  const bookableDeviceIds = useMemo(
    () => bookableDevices.map((d) => d.id).sort(),
    [bookableDevices],
  );
  const deviceAppointmentsParams = useMemo(() => {
    if (!isDevicesTab || bookableDeviceIds.length === 0) return null;
    if (effectiveView === 'Week') {
      return {
        organizationId: orgId,
        practiceId: practiceId,
        filter: 'Week' as const,
        startDate: `${weekStartISO}T00:00:00.000`,
        endDate: `${weekEndISO}T23:59:59.000`,
        devices: bookableDeviceIds,
      };
    }
    return {
      organizationId: orgId,
      practiceId: practiceId,
      filter: 'Day' as const,
      startDate: `${todayStr}T00:00:00.000`,
      endDate: `${todayStr}T23:59:59.000`,
      devices: bookableDeviceIds,
    };
  }, [isDevicesTab, bookableDeviceIds, effectiveView, orgId, practiceId, weekStartISO, weekEndISO, todayStr]);
  const { data: deviceAppointments } = useDeviceAppointments(deviceAppointmentsParams);
  // Same status / invoice pipeline as practitioners — minus the provider
  // filter (a device booking has no provider); the subtype filter narrows
  // COLUMNS, and a hidden column's bookings go with it.
  //
  // NO location stage (PROD-2511). `selectedLocationIds` belongs to the
  // practitioner tab's Office / Home / Virtual buttons, which are not drawn
  // on Devices — a device has no sub-location (PROD-2193). It is persisted
  // (`scheduling-store.ts` partialize), so it survives a reload and carries
  // across tabs: leaving this stage in meant a user who toggled Home on
  // Practitioners and switched to Devices got an EMPTY grid — device
  // bookings are stamped with the Office org-location by
  // `resolveDeviceLocation`, so they match no other id — with no visible
  // control to explain or clear it. Hiding the buttons alone would have made
  // that harder to diagnose, not easier, so the filter goes too.
  const filteredDeviceAppointments = useMemo(() => {
    if (!deviceAppointments) return undefined;
    const stage = filterAppointmentsByStatus(deviceAppointments, selectedStatusCodes) ?? deviceAppointments;
    return filterAppointmentsByInvoiceStatus(stage, selectedInvoiceStatuses);
  }, [deviceAppointments, selectedStatusCodes, selectedInvoiceStatuses]);
  const deviceRows = useMemo(
    () => mapAppointmentsToDayList(filteredDeviceAppointments),
    [filteredDeviceAppointments],
  );
  const rowsByDevice = useMemo(() => groupRowsByDevice(deviceRows), [deviceRows]);
  const deviceDayBounds = useMemo(
    () => ({
      from: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0, 0),
      to: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59, 0),
    }),
    [selectedDate],
  );
  const deviceColumnIds = useMemo(() => deviceColumns.map((d) => d.id), [deviceColumns]);
  const deviceAvailability = useDeviceAvailability({
    practiceId,
    deviceIds: deviceColumnIds,
    from: deviceDayBounds.from,
    to: deviceDayBounds.to,
    // Shading is a Day-grid feature; Week reuses the week grid without it.
    enabled: isDevicesTab && effectiveView === 'Day' && !!practiceId,
  });
  const deviceWeekAppointments = useMemo(() => {
    if (!isDevicesTab || effectiveView !== 'Week') return [];
    const tiles = mapAppointmentsToWeekTiles(filteredDeviceAppointments, `${weekStartISO}T00:00:00`, 7);
    // Week tile = patient · product (no doctor) — CalendarAppointment carries
    // only the name, so the product rides along in it.
    const byId = new Map((filteredDeviceAppointments?.appointments ?? []).map((a) => [a.id, a]));
    return tiles.map((t) => {
      const product = byId.get(t.id)?.appointmentProfile?.profileName;
      return t.isBlock || !product ? t : { ...t, patientName: `${t.patientName} · ${product}` };
    });
  }, [isDevicesTab, effectiveView, filteredDeviceAppointments, weekStartISO]);

  useSchedulingRealtime({
    // The Devices tab merges live updates into ITS list, the Practitioners tab
    // into the practitioner list — same hub, different query. Since PROD-2382
    // those two lists sit on different ENDPOINTS and therefore different key
    // ROOTS, so the whole key is built here by the owning hook's own builder:
    // passing the params alone would have let realtime keep merging under
    // `['appointments', …]` and quietly stop reaching the Devices tab.
    appointmentsQueryKey: isDevicesTab
      ? deviceAppointmentsQueryKey(deviceAppointmentsParams)
      : appointmentsQueryKey(appointmentsParams),
    selectedDateISO: todayStr,
    practiceId: practiceId,
    calendarView: dateFilterToCalendarView(effectiveView),
  });

  // Client-side filter pipeline. Each stage passes the full response
  // through unchanged when its filter set is empty.
  //   1. Provider (specialty ∩ checkbox)
  //   2. Status code   (pills + Statuses dropdown)
  //   3. Location id   (per-location toolbar buttons)
  //   4. Invoice status (Invoice Statuses dropdown)
  // Order doesn't change the result; filters are conjunctive.
  const filteredAppointments = useMemo(() => {
    if (!dayAppointments) return undefined;
    let stage = dayAppointments;
    // Provider filter is ALWAYS authoritative under the new semantic
    // (Andrew, 2026-05-25): Unselect all = empty `selectedProviderIds` =
    // empty `effectivePractitionerIds` = no appointments pass through.
    // Select all = full set = every appointment passes. Removes the prior
    // "empty set means no filter" short-circuit that produced the
    // boxes-unchecked-but-grid-populated bug.
    const set = new Set(effectivePractitionerIds);
    stage = {
      ...stage,
      appointments: (stage.appointments ?? []).filter((a) =>
        appointmentProviderIds(a).some((id) => set.has(id)),
      ),
    };
    stage = filterAppointmentsByStatus(stage, selectedStatusCodes) ?? stage;
    stage = filterAppointmentsByLocation(stage, selectedLocationIds) ?? stage;
    return filterAppointmentsByInvoiceStatus(stage, selectedInvoiceStatuses);
  }, [
    dayAppointments,
    effectivePractitionerIds,
    selectedStatusCodes,
    selectedLocationIds,
    selectedInvoiceStatuses,
  ]);

  // Provider strip override hierarchy (per request):
  //   1. calendarSlots — primary, richer source
  //   2. availableProviders enriched with whatever calendarSlots we have
  //   3. mock fallback
  // When calendarSlots returns first, availableProviders arriving later
  // never overwrites the UI.
  // `calendar-practice-slots` (ON by default since 19 Oct; `?x=-calendar-practice-slots` turns it off): cards follow the VIEWED practice (see PracticeSlotsOptions).
  // Switch on: a failed or still-loading calendarSlots request marks the cards ("Couldn't load slots" + Retry, or a
  // dash), never "Off today · 0 · No appointment profile at this practice"; that state needs a successful answer.
  const slotsOptions = practiceSlotsOptions(practiceSlotsOn, practiceId, calendarSlotsState(isSlotsError, slotsResponse));
  // A non-list answer (a non-JSON 2xx body arrives as a string) is never read as provider data.
  const cardSlots = slotsOptions.slotsState === 'error' && !Array.isArray(slotsResponse) ? undefined : slotsResponse;
  const allProviders: ProviderSummary[] = (() => {
    const fromSlots = providersFromCalendarSlots(cardSlots, selectedDate, slotsOptions);
    if (fromSlots.length > 0) return fromSlots;
    if (apiProviders && apiProviders.length > 0) {
      return enrichProviders(apiProviders, cardSlots, selectedDate, slotsOptions);
    }
    // Empty until either query responds — strip renders nothing rather than
    // flashing fake doctors during the initial load.
    return [];
  })();

  // Flutter parity (`calendar_tab.dart:3420-3425`): on first load with no
  // persisted selection, default to ALL providers selected. Without this the
  // ProviderStrip renders every card unchecked while the grid still shows
  // every appointment (empty `selectedProviderIds` is treated as "no filter"
  // at line ~219). That two-state ambiguity is what Andrew reported — boxes
  // unchecked, grid populated; clicking Select all "fixed" it until navigating
  // away. The ref scopes the effect to one fire per mount so an explicit
  // Unselect-all within the session is respected; the next remount re-fills.
  const didAutoFillProvidersRef = useRef(false);
  useEffect(() => {
    if (didAutoFillProvidersRef.current) return;
    if (allProviders.length === 0) return;
    didAutoFillProvidersRef.current = true;
    const current = useSchedulingStore.getState().selectedProviderIds;
    if (current.length > 0) return;
    useSchedulingStore
      .getState()
      .setSelectedProviders(allProviders.map((p) => p.id));
  }, [allProviders]);

  // Apply the persisted specialty filter (mirrors Flutter — empty = "All").
  // Filtering uses the *visible practitioner ids* derived above, which already
  // accounts for primary + doctorSpecialities; that way a provider whose
  // primary is "General Practice" but who lists "Pediatrics" in
  // doctorSpecialities stays visible when "Pediatrics" is the active chip.
  const providers: ProviderSummary[] =
    selectedSpecialties.length === 0
      ? allProviders
      : allProviders.filter((p) => visiblePractitionerIds.includes(p.id));

  // Specialty chips — same precedence as the strip. Each helper returns
  // ["All", ...] so length > 1 means we got at least one real specialty;
  // length === 1 means only "All" came back (no specialty data on the
  // providers we received). No hardcoded fallback — the bar shows just
  // "All" until the real list arrives.
  const specialties = (() => {
    const fromSlots = specialtiesFromCalendarSlots(slotsResponse);
    if (fromSlots.length > 1) return fromSlots;
    if (apiProviders && apiProviders.length > 0) {
      const fromProviders = specialtiesFromProviders(apiProviders);
      if (fromProviders.length > 1) return fromProviders;
    }
    return ['All'];
  })();

  const statusLegend = statuses?.map((s) => ({ code: s.code, display: s.display })) ?? [];

  // Day/Week-view header strip + appointment tiles.
  //   Day  → 1 column anchored on selectedDate.
  //   Week → 7 columns (Mon…Sun) anchored on `weekStartDate`. dayIndex
  //          per tile comes from `mapAppointmentsToWeekTiles` which
  //          computes the offset from the Monday anchor.
  // Flutter parity: `calendar_tab.dart:2531-2539` for the bounds,
  // `calendar_by_week_widget.dart:217-247` for the 7-column header.
  const todayISO = toLocalISODate(new Date());
  const isSelectedToday = todayStr === todayISO;
  const days =
    calendarView === 'Week'
      ? Array.from({ length: 7 }, (_, i) => {
          const d = addDays(weekStartDate, i);
          return {
            dayOfWeek: d.toLocaleDateString('en-US', { weekday: 'short' }),
            date: d.getDate(),
            isToday: toLocalISODate(d) === todayISO,
          };
        })
      : [
          {
            dayOfWeek: selectedDate.toLocaleDateString('en-US', {
              weekday: 'short',
            }),
            date: selectedDate.getDate(),
            isToday: isSelectedToday,
          },
        ];
  const appointments: CalendarAppointment[] =
    calendarView === 'Week'
      ? mapAppointmentsToWeekTiles(
          filteredAppointments,
          `${weekStartISO}T00:00:00`,
          7,
        )
      : mapAppointmentsToDayTiles(filteredAppointments, todayStr);

  // Day-view list table — one row per appointment, grouped by hour in the
  // component. Time gutter renders earliest→latest hour the rows occupy.
  const dayListRows = useMemo(
    () => mapAppointmentsToDayList(filteredAppointments),
    [filteredAppointments],
  );
  // Day-view gutter range comes from /api/Practice/WorkingHoursList
  // (matches Flutter exactly). Falls back to 8a–8p inside the helper if
  // the response is empty / still loading.
  const dayListHours = useMemo(
    () => hoursForDayList(dayListRows, practiceWorkingHours, selectedDate),
    [dayListRows, practiceWorkingHours, selectedDate],
  );
  const dayListCurrentHour = isSelectedToday
    ? new Date().getHours()
    : undefined;
  // Device day grid gutter: the practice hours ∪ every visible device's own
  // windows, so a chamber that opens at 07:00 is not clipped by a 08:00 gutter.
  const deviceHours = useMemo(() => {
    const base = hoursForDayList(deviceRows, practiceWorkingHours, selectedDate);
    const windows = deviceColumns.flatMap((d) =>
      windowsForDay(parseAvailability(deviceAvailability.byDevice.get(d.id)), selectedDate),
    );
    return hoursCoveringWindows(base, windows);
  }, [deviceRows, practiceWorkingHours, selectedDate, deviceColumns, deviceAvailability.byDevice]);

  // Real userId from the auth store — backend `createdBy` expects a Guid,
  // so the previous "demo-user" placeholder 400ed BookAppointment.
  const authUser = useAuthStore((s) => s.user);
  const authUserId = authUser?.userId ?? '';

  // ── Month view data (PROD-558) ───────────────────────────────────────
  // `calendarView` is declared up at the top of this component (it has
  // to be in scope for `appointmentsParams` which now branches on it
  // for Week view, PROD-952). The month-range block below still gates
  // on `calendarView === 'Month'` — no parallel fetch waste on
  // Day / Week / Doctor / Room.

  // Fetch a month-range AppointmentsRequest anchored on selectedDate.
  // Gated on calendarView === 'Month' so Day / Week / Doctor / Room
  // views don't fire a wasted month request.
  const monthBounds = useMemo(() => {
    const anchor = selectedDate;
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { startISO: toLocalISO(first), endISO: toLocalISO(last) };
  }, [selectedDate]);

  const monthAppointmentsParams = useMemo(
    () =>
      // Gate on view-active AND the standard practitioner-set check —
      // both must hold for the request to fire.
      calendarView === 'Month' && allPractitionerIds.length > 0
        ? {
            organizationId: orgId,
            practiceId: practiceId,
            filter: 'Month' as const,
            startDate: `${monthBounds.startISO}T00:00:00.000`,
            endDate: `${monthBounds.endISO}T23:59:59.000`,
            practitioners: allPractitionerIds,
            // Status + location filters must go SERVER-SIDE for the month view.
            // Unlike Day/Week (which fetch the full list and filter client-side),
            // the Month response caps each day's `appointmentList` at 3 and reports
            // the total in `group.count` — so a client-side filter over the ≤3
            // returned rows would be lossy and the "+N more" count wrong. Passing
            // the selections here lets the backend return correctly-filtered groups
            // with accurate counts. Empty arrays = "no filter" (unchanged default,
            // so no regression to the current all-statuses month view).
            // The request `status` field is `Coding[]` (Flutter sends the full
            // status objects), so map the selected codes → { code, display }
            // (display looked up from the status list, code as fallback).
            status: selectedStatusCodes.map(
              (code): Coding => ({
                code,
                display: statuses?.find((s) => s.code === code)?.display ?? code,
              }),
            ),
            location: selectedLocationIds,
          }
        : null,
    [
      calendarView,
      monthBounds,
      allPractitionerIds,
      orgId,
      practiceId,
      selectedStatusCodes,
      selectedLocationIds,
      statuses,
    ],
  );
  const {
    data: monthAppointments,
    isLoading: monthIsLoading,
    isError: monthIsError,
  } = useAppointments(monthAppointmentsParams);

  const monthGrid: MonthCalendarDay[] = useMemo(() => {
    // PROD-963 + month-parity (Flutter `calendar_by_month_widget.dart`).
    // The Month response is shaped unlike Day/Week — verified against the dev
    // curl (filter:'Month'):
    //   • Appointments live in `appointmentByGroupBy[]`, NOT the flat
    //     `appointments` list. Each group is keyed by `appointmentDate` (the
    //     backend's tz-local day anchor — key cells by THIS, not by each
    //     appointment's `start`, which can fall on the next UTC day).
    //   • The backend caps each group's `appointmentList` at 3 and reports
    //     the overflow in `group.count` → that's the "+N more" number
    //     (Flutter `getMoreAppointment`). Never derive it client-side; we
    //     only receive ≤3 rows.
    //   • Blocks come in a SEPARATE `blockAppointment[]` array keyed by
    //     `blockDate`, with the reason in `note` (not `comment`) and a
    //     `recurringId`. Flutter merges them into each day + sorts by start
    //     (`getMonthAppointment` 317-336).
    // `mapAppointmentsToDayList` is reused for both appointments and blocks
    // (blocks fed as synthetic rows) so time formatting + isBlock/recurring
    // derivation stay shared with the other views.
    const rowToEvent = (r: DayListRow): MonthEvent => ({
      id: r.id,
      label: r.isBlock ? (r.note ?? '') : r.patientName,
      timeRange: r.timeRange,
      profileColor: r.profileColor,
      isBlock: r.isBlock,
      isRecurring: r.isRecurring,
      patientId: r.patientId,
      providerId: r.practitionerId,
      appointmentProfileId: r.appointmentProfileId,
    });
    const startMs = (iso: string | null | undefined) => {
      const t = iso ? new Date(iso).getTime() : NaN;
      return Number.isNaN(t) ? 0 : t;
    };

    // dayISO -> { sortable items, appointment-overflow count }
    const byDay = new Map<string, { items: { ms: number; event: MonthEvent }[]; moreCount: number }>();
    const bucket = (iso: string) => {
      let b = byDay.get(iso);
      if (!b) {
        b = { items: [], moreCount: 0 };
        byDay.set(iso, b);
      }
      return b;
    };

    const groups = monthAppointments?.appointmentByGroupBy ?? [];
    for (const group of groups) {
      // tz assumption (PR #398 review #3): appointments are keyed by
      // `group.appointmentDate` and blocks (below) by `blockDate` — BOTH are
      // the backend's tz-local day anchor (verified: appointmentDate
      // "2026-06-01T00:00:00", blockDate "2026-06-01T15:00:00", same local
      // day). If the backend ever emits one of them in a different tz, a
      // block could land in the wrong cell — revisit this keying then.
      const dayISO = (group.appointmentDate ?? '').slice(0, 10);
      if (!dayISO) continue;
      // Status filtering: the NON-empty case is already applied server-side
      // (monthAppointmentsParams.status). The EMPTY case still needs the
      // client-side "hide Cancelled" default that Flutter's month widget does
      // (`calendar_by_month_widget.dart:346-350`). filterAppointmentsByStatus
      // encodes both: empty → drop Cancelled; non-empty → keep matching (a
      // no-op here since the server already narrowed the group).
      const filteredGroup =
        filterAppointmentsByStatus(
          { appointments: group.appointmentList ?? [] } as unknown as AppointmentsDetailsResponse,
          selectedStatusCodes,
        ) ?? ({ appointments: [] } as unknown as AppointmentsDetailsResponse);
      const rows = mapAppointmentsToDayList(filteredGroup);
      const b = bucket(dayISO);
      for (const r of rows) b.items.push({ ms: startMs(r.startISO), event: rowToEvent(r) });
      // Flutter parity: render every returned appointment + show "+{count} more"
      // using the RAW backend `group.count` — no subtraction
      // (`calendar_by_month_widget.dart:165-220` + `getMoreAppointment:395`).
      // NB: we always render the full returned list, so a low `count` never
      // HIDES a card — it only labels the chip. Surface the anomaly in dev so
      // a backend `count < returned` (partial page / bug) is caught in testing
      // rather than silently mislabeling for a clinician (PR #398 review #1).
      const returned = group.appointmentList?.length ?? 0;
      if (import.meta.env.DEV && (group.count ?? 0) < returned) {
        // eslint-disable-next-line no-console
        console.warn(
          `[month] ${dayISO}: backend count (${group.count}) < returned appointments (${returned}); "+N more" mirrors Flutter's raw count and may understate.`,
        );
      }
      b.moreCount += group.count ?? 0;
    }

    // Legacy fallback: a future variant might return the flat list instead.
    if (groups.length === 0) {
      const rows = mapAppointmentsToDayList(
        filterAppointmentsByStatus(
          { appointments: monthAppointments?.appointments ?? [] } as AppointmentsDetailsResponse,
          selectedStatusCodes,
        ) ?? ({ appointments: [] } as unknown as AppointmentsDetailsResponse),
      );
      for (const r of rows) {
        bucket(r.startISO.slice(0, 10)).items.push({
          ms: startMs(r.startISO),
          event: rowToEvent(r),
        });
      }
    }

    // Blocks — separate array; map as synthetic appointments so the shared
    // mapper formats the time range + flags isBlock/isRecurring.
    const blocks = (monthAppointments?.blockAppointment ?? []) as unknown as MonthBlockAppointment[];
    if (blocks.length > 0) {
      const blockRows = mapAppointmentsToDayList({
        appointments: blocks.map((bl) => ({
          id: bl.id,
          start: bl.startDate,
          end: bl.endDate,
          comment: bl.note,
          isBlock: true,
          recurringId: bl.recurringId ?? undefined,
          participant: [],
        })) as unknown as ListAppointmentResponse[],
      } as AppointmentsDetailsResponse);
      const rowById = new Map(blockRows.map((r) => [r.id, r]));
      for (const bl of blocks) {
        const dayISO = (bl.blockDate ?? bl.startDate ?? '').slice(0, 10);
        const row = rowById.get(bl.id);
        if (!dayISO || !row) continue;
        bucket(dayISO).items.push({ ms: startMs(bl.startDate), event: rowToEvent(row) });
      }
    }

    const dayData = new Map<string, { events: MonthEvent[]; moreCount: number }>();
    for (const [iso, b] of byDay) {
      b.items.sort((x, y) => x.ms - y.ms);
      dayData.set(iso, { events: b.items.map((i) => i.event), moreCount: b.moreCount });
    }

    return buildMonthGrid({
      monthAnchor: selectedDate,
      selectedISO: toLocalISO(selectedDate),
      dayData,
      todayISO: toLocalISO(new Date()),
    });
  }, [monthAppointments, selectedDate, selectedStatusCodes]);

  // Open a month-cell appointment in the SAME anchored popup the day/week/
  // doctor views use (Flutter `onSelect` → `showAppointmentCard`, shared by
  // every view — calendar_tab.dart:6385-6408). Blocks are routed to the
  // block panel by the frame before this fires, so this only sees patient
  // appointments. We already hold all four ids on the event, so set the
  // popup target directly (no dayAppointments lookup needed).
  const handleMonthEventClick = useCallback(
    (event: MonthEvent) => {
      if (!event.patientId || !event.providerId || !event.appointmentProfileId) {
        toast.error("Couldn't read appointment data — please refresh and try again.");
        return;
      }
      setPopupTarget({
        appointmentId: event.id,
        patientId: event.patientId,
        providerId: event.providerId,
        appointmentProfileId: event.appointmentProfileId,
      });
    },
    [],
  );

  const monthLabel = useMemo(
    () =>
      selectedDate
        .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        .toUpperCase(),
    [selectedDate],
  );

  // View-appointment popup (PROD-776). Build the target from the clicked
  // appointment's full record (patientId / providerId / appointmentProfileId
  // all live on the AppointmentResponse) so the popup can POST
  // PreLoadAppointmentDetails with the right ids.
  const [popupTarget, setPopupTarget] = useState<ViewAppointmentTarget | null>(null);
  // Follow-up handoff (PROD-987) — the popup's "Follow-up Appointment"
  // action opens the central modal pre-filled from the fulfilled appointment.
  const [popupModalBooking, setPopupModalBooking] = useState<{ initialValues: ComponentProps<typeof BridgeCreateAppointmentModal>['initialValues'] } | null>(null);
  // Legacy fallback when true is off.
  const requestCreateAppointment = useCreateAppointmentHandoff(
    (s) => s.requestCreateAppointment,
  );
  const openPopupForAppointment = useCallback(
    (appointmentId: string) => {
      const apt =
        (isDevicesTab ? deviceAppointments : dayAppointments)?.appointments?.find(
          (a) => a.id === appointmentId,
        ) ?? dayAppointments?.appointments?.find((a) => a.id === appointmentId);
      // Cache-miss guard — happens if the click fires between a background
      // refetch invalidating the list and the new response landing. Surface
      // an actionable toast instead of opening a blank popup that would
      // sit forever on the loading skeleton (the details query is gated
      // on all 4 ids being present, so an incomplete target would never
      // resolve). User can re-click after the list refreshes.
      if (!apt) {
        toast.error('Appointment list is refreshing. Click again in a moment.');
        return;
      }
      const patientId = findPatientParticipant(apt.participant)?.patient?.id;
      const providerId = apt.participant?.find((p) => {
        const t = (p as { referenceType?: string }).referenceType?.toLowerCase();
        return t === 'practitioner' || t === 'provider';
      })?.provider?.id;
      const appointmentProfileId = apt.appointmentProfile?.id;
      // Device booking (PROD-2007): a `Device` participant and NO provider.
      // The details endpoint binds a Guid providerId, so the empty GUID
      // travels in its place and the popup renders the device instead.
      const deviceId = providerId ? undefined : deviceIdFromParticipants(apt.participant);
      if (deviceId && patientId && appointmentProfileId) {
        setPopupTarget({
          appointmentId,
          patientId,
          providerId: EMPTY_GUID,
          appointmentProfileId,
          deviceId,
          deviceName: allDevices.find((d) => d.id === deviceId)?.name,
          // The popup renders from THIS row until (or unless) the preload
          // resolves — `PreLoadAppointmentDetails` binds a non-nullable Guid
          // providerId and a device booking has none, so a rejection is
          // plausible and must not take the popup's actions down with it
          // (review blocking #1). A list row structurally carries every field
          // the left column and the footer actions read; the preload-only
          // extras (invoice, patient chips, sticker) stay behind the query.
          fallbackAppt: apt as unknown as AppointmentResponse,
        });
        return;
      }
      // Same guard for malformed participants — surface explicitly rather
      // than silently dropping the click. Should be impossible on
      // production data but defensive against shape drift.
      if (!patientId || !providerId || !appointmentProfileId) {
        toast.error("Couldn't read appointment data — please refresh and try again.");
        return;
      }
      setPopupTarget({
        appointmentId,
        patientId,
        providerId,
        appointmentProfileId,
      });
    },
    [dayAppointments, deviceAppointments, isDevicesTab, allDevices],
  );
  /**
   * Navigate to the FULL tabbed appointment screen on a given tab
   * (`/scheduling/appointment/:id?patientId&providerId&profileId&tab&from`).
   *
   * One helper, two entry points that must not drift: the popup's pencil when
   * it asks for a non-default tab, and the Payment panel's "Open in tab".
   * Works for a device booking as well as a practitioner appointment — the
   * ids it ships are the same four the popup itself preloads with, and a
   * device's `providerId` is the empty GUID rather than absent.
   */
  const openAppointmentTab = useCallback(
    (tab: string) => {
      if (!popupTarget) return;
      const params = new URLSearchParams({
        patientId: popupTarget.patientId,
        providerId: popupTarget.providerId,
        profileId: popupTarget.appointmentProfileId,
      });
      params.set('tab', tab);
      // Return context so the screen's in-app Back lands on this calendar
      // day/view (not the default today) AND on the tab the user opened it
      // from. Andrew, 2026-09-14: "the opener screen should be the return
      // screen when pressing back" -- a device booking opened from the Devices
      // tab was returning to Practitioners, because the actor was the one bit
      // of calendar context this URL did not carry. Practitioners is the
      // ABSENCE of the param (see `calendarActor` above), so the practitioner
      // return path stays byte-identical.
      const back = new URLSearchParams({
        date: toLocalISODate(selectedDate),
        view: calendarView,
      });
      if (isDevicesTab) back.set(CALENDAR_ACTOR_PARAM, 'devices');
      params.set('from', `/?${back.toString()}`);
      navigate(`/scheduling/appointment/${popupTarget.appointmentId}?${params.toString()}`);
    },
    [popupTarget, selectedDate, calendarView, isDevicesTab, navigate],
  );
  // PROD-2526 — a device booking's time-only moves (drag-and-drop) go through
  // the DEVICE route, `PUT /api/Appointment/Resource/{id}`. They used to call
  // the provider's `PUT /api/Appointment/Reschedule`. The popup's Reschedule
  // no longer has a device dialog of its own: it opens the Book-a-device rail
  // in edit mode, the same rail that booked it (see `onReschedule` below).
  const deviceReschedule = useDeviceReschedule();

  // ── Drag-drop reschedule (PROD-563 + PROD-562 wiring) ──────────────
  // Looks up the source appointment from the cached day list, computes
  // the matching endDate by preserving the original duration, then
  // fires the appropriate mutation:
  //
  //   • Non-recurring tile → fire `useRescheduleAppointment` directly
  //     (the original PROD-563 path).
  //   • Recurring tile → pop the scope picker first, then on confirm:
  //       - `this-only`        → `useRescheduleAppointment` (same as
  //         non-recurring; backend treats the PUT as a single-occurrence
  //         reschedule).
  //       - `following` / `all` → `useSeriesReschedule.fire(...)`. The
  //         hook pre-fetches the FULL appointment body via fetchQuery
  //         against the appointment-details endpoint, then fires
  //         `useUpdateAppointment` with the full body + start/end
  //         overwritten + the editRecurring payload. The pre-fetch
  //         eliminates the 400 risk a cached `ListAppointmentResponse`
  //         subset could carry — the panel save flow's verified curl
  //         (PR #100) used the full preload shape, so we mirror that
  //         for drag-drop series-wide too.
  //
  // Cache invalidation note (Samer PR #281 review §4): no manual
  // queryClient ops in this file. The reschedule hook owns its own
  // invalidation (onSettled invalidates ['appointments'] +
  // ['appointment-details']).
  //
  // Drag-drop reschedules the SINGLE occurrence — recurring or not (Flutter
  // parity: `QuickRescheduleAppointmentEvent` → PUT /Appointment/Reschedule
  // with just {id,start,end}, no scope prompt). Series-wide changes live in
  // the recurring-series edit menu, not the drag gesture.
  const reschedule = useRescheduleAppointment();

  const fireRescheduleThisOnly = useCallback(
    (id: string, newStartISO: string, newEndISO: string) => {
      reschedule.mutate(
        {
          appointmentId: id,
          startDate: newStartISO,
          endDate: newEndISO,
        },
        {
          onSuccess: () => toast.success('Appointment rescheduled'),
          onError: () => toast.error("Couldn't reschedule — try again."),
        },
      );
    },
    [reschedule],
  );

  const handleAppointmentDrop = useCallback(
    ({ id, newStartISO }: { id: string; newStartISO: string; dayIndex: number }) => {
      // Pure routing decision — keeps the missing-context check
      // unit-testable without rendering the page (see `drop-routing.test.ts`).
      const route = routeAppointmentDrop({
        dayAppointments,
        event: { id, newStartISO },
      });

      if (route.kind === 'error') {
        toast.error(route.message);
        return;
      }

      // Week drag reschedules the single occurrence directly — recurring or
      // not (Flutter parity; no scope prompt on drag).
      fireRescheduleThisOnly(
        route.payload.id,
        route.payload.newStartISO,
        route.payload.newEndISO,
      );
    },
    [dayAppointments, fireRescheduleThisOnly],
  );

  // By-Doctor drag-reschedule (PROD-984). Same routing as the Week drag, but
  // a non-recurring drop opens a confirm dialog FIRST (Flutter By-Doctor
  // parity — `calendar_by_doctor_widget.dart:910-927` confirms every drag;
  // the Week drag has no confirm). Recurring still defers to the scope dialog.
  const [confirmDrop, setConfirmDrop] = useState<{
    id: string;
    newStartISO: string;
    newEndISO: string;
    oldStartISO: string | null;
    providerName: string | null;
    /** Set for a device-column drag (PROD-2007) — the confirm names the device. */
    deviceName?: string | null;
    /**
     * PROD-2526 — a device booking's drop. Its presence routes the confirm to
     * the device route instead of the provider's `PUT /Appointment/Reschedule`.
     */
    deviceSource?: {
      id: string;
      participant?: { referenceType?: string | null; patient?: { id?: string | null } | null }[] | null;
      appointmentProfile?: { id?: string | null } | null;
    } | null;
    patientName: string | null;
  } | null>(null);

  // Device column drag (PROD-2007) — the same time-only reschedule + confirm
  // as By-Doctor, looked up in the DEVICE list; the device never changes.
  const handleDeviceDrop = useCallback(
    ({ id, newStartISO }: { id: string; newStartISO: string }) => {
      if (confirmDrop) {
        toast.message('Finish the open reschedule first.');
        return;
      }
      const route = routeAppointmentDrop({
        dayAppointments: deviceAppointments,
        event: { id, newStartISO },
      });
      if (route.kind === 'error') {
        toast.error(route.message);
        return;
      }
      const apt = deviceAppointments?.appointments?.find((a) => a.id === id);
      const pat = findPatientParticipant(apt?.participant)?.patient;
      const deviceId = deviceIdFromParticipants(apt?.participant);
      setConfirmDrop({
        id: route.payload.id,
        newStartISO: route.payload.newStartISO,
        newEndISO: route.payload.newEndISO,
        oldStartISO: apt?.start ?? null,
        providerName: null,
        deviceName: allDevices.find((d) => d.id === deviceId)?.name ?? 'the device',
        deviceSource: apt
          ? {
              id: apt.id,
              participant: apt.participant as never,
              appointmentProfile: apt.appointmentProfile ?? null,
            }
          : null,
        patientName: [pat?.firstName, pat?.lastName].filter(Boolean).join(' ') || null,
      });
    },
    [deviceAppointments, confirmDrop, allDevices],
  );

  const handleByDoctorDrop = useCallback(
    ({ id, newStartISO }: { id: string; newStartISO: string }) => {
      // Guard: ignore a new drop while a confirm dialog is already open.
      if (confirmDrop) {
        toast.message('Finish the open reschedule first.');
        return;
      }
      const route = routeAppointmentDrop({
        dayAppointments,
        event: { id, newStartISO },
      });
      if (route.kind === 'error') {
        toast.error(route.message);
        return;
      }
      // Confirm before firing (Flutter By-Doctor parity) — recurring or not,
      // the drag moves the single occurrence.
      const apt = dayAppointments?.appointments?.find((a) => a.id === id);
      const part = (apt as { participant?: Array<{ referenceType?: string; provider?: { name?: string }; patient?: { firstName?: string; lastName?: string } }> } | undefined)?.participant;
      const prov = part?.find((p) => (p.referenceType ?? '').toLowerCase() === 'provider')?.provider;
      const pat = findPatientParticipant(part)?.patient;
      setConfirmDrop({
        id: route.payload.id,
        newStartISO: route.payload.newStartISO,
        newEndISO: route.payload.newEndISO,
        oldStartISO: apt?.start ?? null,
        providerName: prov?.name ?? null,
        patientName: [pat?.firstName, pat?.lastName].filter(Boolean).join(' ') || null,
      });
    },
    [dayAppointments, confirmDrop],
  );

  return (
    <>
    <FrameScheduling
      providers={providers}
      days={days}
      appointments={appointments}
      dayListRows={dayListRows}
      dayListHours={dayListHours}
      dayListCurrentHour={dayListCurrentHour}
      specialties={specialties}
      statusLegend={statusLegend}
      organizationLocations={organizationLocations}
      selectedDate={selectedDate}
      onSelectedDateChange={setSelectedDate}
      practiceName={authUser?.practiceName ?? ''}
      organizationId={orgId}
      practiceId={practiceId}
      userId={authUserId}
      listWaitingRoom={dayAppointments?.waitingRoom ?? 0}
      listNoShow={dayAppointments?.noShow ?? 0}
      listCancelled={dayAppointments?.cancelled ?? 0}
      monthGrid={monthGrid}
      onMonthEventClick={handleMonthEventClick}
      monthLabel={monthLabel}
      monthIsLoading={monthIsLoading}
      dayListIsLoading={dayAppointmentsLoading}
      providersLoading={isProvidersLoading}
      onRetryProviderSlots={() => void refetchSlots()}
      providerSlotsRetrying={isSlotsError && isSlotsFetching}
      devicesTabPending={devicesSettingLoading}
      monthIsError={monthIsError}
      calendarView={effectiveView}
      onCalendarViewChange={setCalendarView}
      roomColumns={roomColumns}
      devicesTab={devicesEnabled ? {
        actor: calendarActor,
        onActorChange: setCalendarActor,
        devices: bookableDevices,
        blockableDevices,
        deviceCount,
        cards: deviceCards,
        columns: deviceColumns,
        rowsByDevice,
        availabilityByDevice: deviceAvailability.byDevice,
        subtypes: deviceSubtypeOptions,
        selectedSubtypes: selectedDeviceSubtypes,
        onSubtypeToggle: toggleDeviceSubtype,
        onSubtypeClear: clearDeviceSubtypes,
        isLoading: devicesLoading,
        hours: deviceHours,
        weekAppointments: deviceWeekAppointments,
        onDeviceAppointmentDrop: handleDeviceDrop,
      } : undefined}
      onMonthDayClick={(iso) => {
        // Parse YYYY-MM-DD into a local Date (avoid timezone shift).
        // Overflow cells (prev/next-month days in the 42-cell grid) are
        // valid click targets — clicking one anchors selectedDate to
        // that date which IS in a different month. The monthBounds memo
        // recomputes on the next render and the month query refetches
        // for the new month. Flutter parity:
        // `calendar_by_month_widget.dart` (Samer review #81 §3).
        const [y, m, d] = iso.split('-').map(Number);
        setSelectedDate(new Date(y, m - 1, d));
      }}
      onSearchPatients={noopSearchPatients}
      onAppointmentClick={openPopupForAppointment}
      onAppointmentDrop={handleAppointmentDrop}
      onByDoctorAppointmentDrop={handleByDoctorDrop}
      onProviderNameClick={canManageDoctors ? handleProviderNameClick : undefined}
    />
    <ViewAppointmentPopup
      open={!!popupTarget}
      onOpenChange={(next) => {
        if (!next) setPopupTarget(null);
      }}
      target={popupTarget}
      // The pencil / edit chips, for EITHER actor.
      //
      // This used to be disabled for a device booking, which had no editable
      // rail. Since PROD-2526 it opens the Book-a-device rail itself in edit
      // mode, reached through the same `openEditPanel` target;
      // `InlineEditAppointmentPanel` picks the rail from the loaded
      // appointment's participants. `providerId` is the empty GUID for a
      // device, which is exactly what `PreLoadAppointmentDetails` binds and
      // what PROD-2179 taught it to skip its provider-scoped reads on.
      onEdit={(tab) => {
        if (!popupTarget) return;

        // Explicit non-default tab wants the FULL tabbed appointment screen —
        // that's the 3-dots destination in Flutter. Keep navigating for that
        // case. ("Open in tab" comes through `onOpenInTab` instead.)
        if (tab && tab !== 'patient') {
          openAppointmentTab(tab);
          return;
        }

        // Pencil / add-info chips → open the inline LEFT edit panel in place
        // on the calendar (Flutter parity, `calendar_tab.dart:6438` →
        // ChangeLeftPanelTypeEvent(false) + PreLoadAppointmentEvent(showSidePanel)).
        // No navigation — the 3-dots menu remains the path to the full screen.
        openEditPanel({
          appointmentId: popupTarget.appointmentId,
          patientId: popupTarget.patientId,
          providerId: popupTarget.providerId,
          profileId: popupTarget.appointmentProfileId,
          mode: 'edit',
        });
      }}
      onReschedule={() => {
        if (!popupTarget) return;
        // Reschedule (Booked) → open the inline rail in reschedule mode on the
        // calendar (Flutter parity), same as the pencil but constrained — for
        // EITHER actor. PROD-2526: a device booking used to get a separate
        // date/time dialog on the provider's `PUT /Appointment/Reschedule`; it
        // now opens the Book-a-device rail itself, prefilled, and saves through
        // the device route (`InlineEditAppointmentPanel` picks the rail from the
        // loaded appointment; `providerId` is the empty GUID for a device).
        openEditPanel({
          appointmentId: popupTarget.appointmentId,
          patientId: popupTarget.patientId,
          providerId: popupTarget.providerId,
          profileId: popupTarget.appointmentProfileId,
          mode: 'reschedule',
        });
      }}
      onPrintSticker={() => {
        /* TODO PROD-776 follow-up — sticker print integration */
      }}
      // "Open in tab" beside the Payment header — for EITHER actor. Separate
      // from `onEdit` on purpose: `onEdit` means "editable" (device: no), this
      // means "the full screen exists" (device: yes). The route's own gates
      // are `!!providerId`, which the empty GUID a device booking travels with
      // satisfies, and PROD-2179 made PreLoadAppointmentDetails skip its two
      // provider-scoped reads for Guid.Empty.
      onOpenInTab={(tab) => openAppointmentTab(tab)}
      onFollowUp={(appt) => {
        const userLocations = authUser?.organizationLocations ?? [];
        const firstLocation = userLocations[0]?.id ? userLocations[0] : null;
        const seedISO = followUpSeedDateISO(selectedDate, new Date());
        const initial = buildFollowUpInitialFromAppointment(appt, firstLocation, seedISO);
        if (MODAL_BOOKING) {
          setPopupModalBooking({ initialValues: initial });
        } else {
          requestCreateAppointment(initial);
        }
        setPopupTarget(null);
      }}
    />

    {/* By-Doctor drag-reschedule confirm (PROD-984). Flutter By-Doctor
        confirms every drag (calendar_by_doctor_widget.dart:910-927); the
        non-recurring branch routes here before firing the reschedule. */}
    <AlertDialog
      open={!!confirmDrop}
      onOpenChange={(open) => {
        if (!open) setConfirmDrop(null);
      }}
    >
      <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <AlertDialogHeader>
          <AlertDialogTitle>Reschedule appointment?</AlertDialogTitle>
          <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
            {confirmDrop
              ? `Move ${
                  confirmDrop.patientName
                    ? `${confirmDrop.patientName}'s appointment`
                    : 'this appointment'
                } from ${formatDropTime(confirmDrop.oldStartISO)} to ${formatDropTime(
                  confirmDrop.newStartISO,
                )}?${
                  confirmDrop.providerName
                    ? ` It stays assigned to ${confirmDrop.providerName} — only the time changes.`
                    : confirmDrop.deviceName
                      ? ` It stays on ${confirmDrop.deviceName} — only the time changes.`
                      : ' Only the time changes — the provider is unchanged.'
                }`
              : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (!confirmDrop) return;
              if (confirmDrop.deviceSource) {
                // PROD-2526 — the device route. A course session moves alone,
                // as a practitioner's drag does (no scope prompt on a drag).
                const { deviceSource, newStartISO, newEndISO } = confirmDrop;
                void deviceReschedule.fire(deviceSource, newStartISO, newEndISO).then((res) => {
                  if (res.ok) toast.success('Appointment rescheduled');
                  else toast.error(res.message ?? "Couldn't reschedule — try again.");
                });
                setConfirmDrop(null);
                return;
              }
              fireRescheduleThisOnly(
                confirmDrop.id,
                confirmDrop.newStartISO,
                confirmDrop.newEndISO,
              );
              setConfirmDrop(null);
            }}
          >
            Reschedule
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Central booking modal — follow-up from the popup opens here instead
        of the side panel (Antonin, 14 Oct). */}
    {MODAL_BOOKING && popupModalBooking && (
      <BridgeCreateAppointmentModal
        open
        onOpenChange={(open) => { if (!open) setPopupModalBooking(null); }}
        organizationId={orgId}
        practiceId={practiceId}
        userId={authUserId}
        onSearchPatients={async () => []}
        initialValues={popupModalBooking.initialValues}
        onSaveSuccess={() => setPopupModalBooking(null)}
        closeOnSaveSuccess
      />
    )}
    </>
  );
}

/** Human-readable date+time for the reschedule confirm dialog. Accepts the
 *  no-Z local ISO the calendar uses; null/blank → "—". */
function formatDropTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: getTimeFormat() === '12h',
  });
}
